import { FC, useState, useEffect } from "react"
import { ConnectorFormValues } from "../../../../../../types"
import {
  useFindConnectorLogsQuery,
  useLazyFetchLeadDetailsQuery,
  useLazyFetchSubscriptionDetailsQuery,
  useFetchLeadsByBrandQuery,
  useFetchSubscriptionsByBrandQuery
} from "../../../../../../redux/queries"
import { useConnectorTesting } from "../hooks/useConnectorTesting"
import { useLogPolling } from "../hooks/useLogPolling"
import { useTemplatePreview } from "../hooks/useTemplatePreview"
import OutgoingTestPanel from "./components/OutgoingTestPanel"
import IncomingMonitorPanel from "./components/IncomingMonitorPanel"

interface Props {
  values: ConnectorFormValues
  isCreate: boolean
  isPanelOpen: boolean
}

const TestConnector: FC<Props> = ({ values, isCreate, isPanelOpen }) => {
  const [expandedLogId, setExpandedLogId] = useState<string>("")
  const [recordDetails, setRecordDetails] = useState<any>(null)

  const [fetchLeadDetails, { isLoading: isFetchingLead }] =
    useLazyFetchLeadDetailsQuery()
  const [fetchSubscriptionDetails, { isLoading: isFetchingSubscription }] =
    useLazyFetchSubscriptionDetailsQuery()

  // Fetch recent leads for testing
  const { data: recentLeads } = useFetchLeadsByBrandQuery(values.brand_id, {
    skip: !values.brand_id || values.data_type !== "lead"
  })

  // Fetch recent subscriptions for testing
  const { data: recentSubscriptions } = useFetchSubscriptionsByBrandQuery(
    values.brand_id,
    {
      skip:
        !values.brand_id ||
        (values.data_type !== "subscription" &&
          values.data_type !== "pending_subscription")
    }
  )

  const isFetchingRecord = isFetchingLead || isFetchingSubscription

  // Poll logs for incoming connectors
  const { data: logsData, refetch: refetchLogs } = useFindConnectorLogsQuery(
    { connector_id: values.id, limit: 5 },
    { skip: !values.id || values.direction !== "incoming" }
  )

  // Custom hooks for business logic
  const {
    selectedRecordId,
    setSelectedRecordId,
    testResult,
    isTesting,
    handleTestOutgoing,
    clearSelection
  } = useConnectorTesting()

  const { isPolling, startPolling, stopPolling } = useLogPolling({
    enabled: values.direction === "incoming",
    refetchLogs
  })

  const { mappedPreview, showPreview } = useTemplatePreview({
    selectedRecordId,
    dataType: values.data_type,
    template: values.template,
    fieldMapping: values.field_mapping,
    connectorId: values.id,
    isPanelOpen
  })

  const handleViewRecord = async (
    recordId: string,
    recordType: string,
    logId: string
  ) => {
    if (expandedLogId === logId) {
      setExpandedLogId("")
      setRecordDetails(null)
      return
    }

    setExpandedLogId(logId)
    setRecordDetails(null)

    try {
      if (recordType === "lead") {
        const result = await fetchLeadDetails(recordId).unwrap()
        setRecordDetails(result)
      } else if (
        recordType === "subscription" ||
        recordType === "pending_subscription"
      ) {
        const result = await fetchSubscriptionDetails({ id: recordId }).unwrap()
        setRecordDetails(result)
      }
    } catch (error) {
      setRecordDetails({ error: "Failed to load record details" })
    }
  }

  // Clear all test state when panel closes
  useEffect(() => {
    if (!isPanelOpen) {
      clearSelection()
      setExpandedLogId("")
      setRecordDetails(null)
      if (isPolling) {
        stopPolling()
      }
    }
  }, [isPanelOpen, clearSelection, isPolling, stopPolling])

  if (isCreate) {
    return (
      <div className="bg-gray-50 border border-gray-300 rounded-md p-4 text-center text-gray-600">
        Save the connector first to enable testing
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Outgoing Connector Test */}
      {values.direction === "outgoing" && (
        <OutgoingTestPanel
          dataType={values.data_type}
          selectedRecordId={selectedRecordId}
          onSelectRecord={setSelectedRecordId}
          recentLeads={recentLeads || []}
          recentSubscriptions={recentSubscriptions || []}
          showPreview={showPreview}
          mappedPreview={mappedPreview}
          isTesting={isTesting}
          onTest={() => handleTestOutgoing(values.id || "")}
          onClear={clearSelection}
          testResult={testResult}
        />
      )}

      {/* Incoming Connector Test */}
      {values.direction === "incoming" && (
        <div className="space-y-3">
          <IncomingMonitorPanel
            isPolling={isPolling}
            onStartPolling={startPolling}
            onStopPolling={stopPolling}
            logsData={logsData}
            expandedLogId={expandedLogId}
            isFetchingRecord={isFetchingRecord}
            recordDetails={recordDetails}
            onViewRecord={handleViewRecord}
          />
        </div>
      )}
    </div>
  )
}

export default TestConnector
